// function to convert const names to normalized status
export function normalizedWord(status: string): string {
  return status?.toLowerCase().replace(/_/g, " ")?.split(" ").map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(" ") || "Unknown";
}

// EHR-native display labels for MDS assessment status codes (NETSOL-11379).
// The EHR emits a two-character code as `status`; the backend collapses
// several codes onto the same `status_normalized` bucket (e.g. 11, 12 and
// 17 all map to PENDING_SUBMISSION — the facility's work on the assessment
// is finished and it is now waiting on CMS, not on a clinician), which
// loses information when shown to users. Keys here mirror the
// MDS_STATUS_MAP in api/events/mds_event_processor.py so the queue view
// matches the EHR.
export const MDS_EHR_STATUS_LABELS: Record<string, string> = {
  "00": "Incomplete",
  "01": "Audit Failed",
  "02": "Audit Passed; Ready for Completion",
  "11": "Complete; Ready for Submission",
  "12": "Placed in EDS Batch",
  "13": "Inactivation Pending",
  "14": "Inactive",
  "15": "Complete; Submission Not Required",
  "16": "Accepted by CMS",
  "17": "Pending review by CMS",
  "18": "Rejected by CMS",
  "19": "FVR available",
  "90": "Struck/Deleted",
  "91": "Struck/Deleted",
  "92": "Struck/Deleted",
};

/**
 * Filter-dropdown options for the MDS tab (NETSOL-11379 migration).
 *
 * After the `status` field migration to raw EHR codes, the MDS tab's status
 * filter offers every distinct EHR-native label so users can slice by the
 * specific state (e.g. "Placed in EDS Batch") rather than the coarser
 * bucket ("Ready For Review"). The backend routes a code value to the
 * `status` field; enum values from the Care tab continue to route to
 * `status_normalized`.
 *
 * "Struck/Deleted" is deduped to a single option whose value is the
 * comma-separated list of codes (90,91,92). The work-items endpoint splits
 * on commas already, so one click filters all three codes at once.
 *
 * "Queued" maps to the literal string "QUEUED" — our pre-EHR-event
 * workflow state, stored literally in `status` (not a raw code, not an
 * enum bucket).
 *
 * "all" is the same "Open Items" sentinel every other tab's status filter
 * gets for free from `toFilterOptions()` (see useFilterOptions.ts) — the
 * default, no-filter-applied view. Because the MDS tab hand-lists its
 * options here instead of going through that helper, it must be listed
 * explicitly or the "Open Items" choice silently disappears from the
 * dropdown (it would still work as an empty-filter value, just with
 * nothing in the list to select it from).
 */
export interface FilterStatusOption {
  value: string;
  label: string;
}

export const MDS_FILTER_STATUS_OPTIONS: readonly FilterStatusOption[] = [
  { value: "all", label: "Open Items" },
  { value: "QUEUED", label: "Queued" },
  { value: "00", label: "Incomplete" },
  { value: "01", label: "Audit Failed" },
  { value: "02", label: "Audit Passed; Ready for Completion" },
  { value: "11", label: "Complete; Ready for Submission" },
  { value: "12", label: "Placed in EDS Batch" },
  { value: "13", label: "Inactivation Pending" },
  { value: "14", label: "Inactive" },
  { value: "15", label: "Complete; Submission Not Required" },
  { value: "16", label: "Accepted by CMS" },
  { value: "17", label: "Pending review by CMS" },
  { value: "18", label: "Rejected by CMS" },
  { value: "19", label: "FVR available" },
  { value: "90,91,92", label: "Struck/Deleted" },
];

/**
 * Raw codes whose normalized bucket the backend hides from the MDS default
 * view — COMPLETED and PENDING_SUBMISSION. Used by the "Show Recently
 * Complete" checkbox on the MDS tab so toggling it adds or removes exactly
 * those rows — expressed as raw codes, consistent with everything else in
 * the MDS filter.
 *
 * Deliberately does NOT include Struck/Deleted (90,91,92). Struck items are
 * a distinct concept from completed/pending-submission ones, and bundling
 * them into this checkbox meant there was no way to see recently-completed
 * rows without also pulling in struck/deleted ones. Struck/Deleted remains
 * its own explicit option in MDS_FILTER_STATUS_OPTIONS for users who want it.
 */
export const MDS_COMPLETED_FILTER_VALUES: readonly string[] = [
  "11", // Complete; Ready for Submission     (bucket: PENDING_SUBMISSION)
  "12", // Placed in EDS Batch                (bucket: PENDING_SUBMISSION)
  "15", // Complete; Submission Not Required  (bucket: COMPLETED)
  "16", // Accepted by CMS                    (bucket: COMPLETED)
  "17", // Pending review by CMS              (bucket: PENDING_SUBMISSION)
  "19", // FVR available                      (bucket: COMPLETED)
];

// Resolve the display status for an MDS assessment row. Prefers the
// EHR-native label keyed off the raw status code; falls back to the
// title-cased normalized status when the code is missing or unknown
// (e.g. work items in "QUEUED" that have not yet received an EHR
// status update).
export function mdsAssessmentStatusLabel(
  statusCode: string | null | undefined,
  statusNormalized: string,
): string {
  const code = statusCode?.trim();
  if (code && MDS_EHR_STATUS_LABELS[code]) {
    return MDS_EHR_STATUS_LABELS[code];
  }
  return normalizedWord(statusNormalized);
}
